import { useTranslation } from 'react-i18next';
import { Puzzle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Switch
} from '@bliks-io/ui';
import type { InstalledAppEntry } from '@/lib/app-registry';

export type AppAiFeatureSwitches = Record<string, Record<string, boolean>>;

interface AppAiFeaturesCardProps {
  apps: InstalledAppEntry[];
  value: AppAiFeatureSwitches;
  onToggle: (appId: string, key: string, on: boolean) => void;
  className?: string;
}

const LABEL_LANGUAGES = ['de', 'en', 'fr', 'hu'] as const;
type LabelLanguage = (typeof LABEL_LANGUAGES)[number];
const labelLanguageOf = (language: string): LabelLanguage =>
  LABEL_LANGUAGES.find((l) => language.startsWith(l)) ?? 'en';

/**
 * The AI features the tenant's apps declare (plan 046), one switch each — the
 * app counterpart of the Bliksy and step-name switches, and like them off
 * until switched on. Renders nothing when no app declares any.
 */
export function AppAiFeaturesCard({ apps, value, onToggle, className }: AppAiFeaturesCardProps) {
  const { t, i18n } = useTranslation();
  const withFeatures = apps.filter((app) => (app.aiFeatures ?? []).length > 0);
  if (withFeatures.length === 0) return null;
  const language = labelLanguageOf(i18n.language);
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Puzzle className="w-5 h-5 text-muted-foreground" />
          {t('OrgSettingPage.ai.appsTitle')}
        </CardTitle>
        <CardDescription>{t('OrgSettingPage.ai.appsDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {withFeatures.map((app) => (
          <div key={app.appId} className="space-y-3">
            <div className="text-sm font-semibold">{app.name}</div>
            {(app.aiFeatures ?? []).map((feature) => {
              const id = `ai-feature-${app.appId}-${feature.key}`;
              return (
                <div key={feature.key} className="flex items-center justify-between">
                  <Label htmlFor={id} className="text-base font-medium">
                    {feature.label[language]}
                  </Label>
                  <Switch
                    id={id}
                    checked={value[app.appId]?.[feature.key] ?? false}
                    onCheckedChange={(on) => onToggle(app.appId, feature.key, on)}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
